// src/pages/Product.jsx

import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShopContext } from '../context/ShopContext';
import RelatedProducts from '../components/RelatedProducts';
import {
  Box,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  ButtonGroup,
  Grid,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';

const ProductContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  backdropFilter: 'blur(4px)',
  '-webkit-backdrop-filter': 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
}));

const Thumbnail = styled('img')(({ theme, selected }) => ({
  width: 80,
  height: 80,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.light,
  },
}));

const MainImage = styled('img')({
  width: '100%',
  height: 'auto',
  maxHeight: 500,
  objectFit: 'cover',
  borderRadius: 12,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
});

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.Mui-selected, &.Mui-selected:hover': {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
}));

const Product = () => {
  const { id: productId } = useParams();
  const { 
    currency, 
    addToCart, 
    cartItems, 
    removeFromCart, 
    productData, 
    getProductDataById, 
    loading, 
    error
  } = useContext(ShopContext);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    if (productId) {
      getProductDataById(productId);
    }
  }, [productId, getProductDataById]);

  useEffect(() => {
    if (productData) {
      if (productData.image && productData.image.length > 0) {
        setSelectedImage(productData.image[0]);
      }
      const cartData = cartItems[productData.id];
      if (cartData) {
        const sizesInCart = Object.keys(cartData);
        if (sizesInCart.length > 0) {
          setSelectedSize(sizesInCart[0]);
        }
      } else {
        setSelectedSize(null);
      }
    }
  }, [productData, cartItems]);

  const handleSizeChange = (event, newSize) => {
    setSelectedSize(newSize);
  };

  const handleAddToCart = (e) => {
    e?.stopPropagation?.();
    if (selectedSize) {
      addToCart(productData.id, selectedSize);
    } else if (productData.sizes?.length > 0) {
      // If there are sizes but none selected, select the first one and add to cart
      const firstSize = productData.sizes[0];
      setSelectedSize(firstSize);
      addToCart(productData.id, firstSize);
    } else {
      // If no sizes available, add to cart without size
      addToCart(productData.id, 'one size');
    }
  };

  const handleRemoveFromCart = (e) => {
    e?.stopPropagation?.();
    if (selectedSize) {
      removeFromCart(productData.id, selectedSize);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><Typography>Loading...</Typography></Box>;
  if (error) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><Typography color="error">{error}</Typography></Box>;
  if (!productData) return null;

  const cartDataForThisProduct = cartItems[productData.id] || {};
  // Calculate total quantity across all sizes for this product
  const totalQuantityInCart = Object.values(cartDataForThisProduct).reduce((sum, qty) => sum + qty, 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, backgroundColor: '#f9f9f9' }}>
      <ProductContainer>
        <Grid container spacing={5}>
          {/* Image Gallery */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedImage && <MainImage src={selectedImage} alt={productData.name} />}
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                {productData.image.map((img, index) => (
                  <Thumbnail
                    key={index}
                    src={img}
                    alt={`${productData.name} thumbnail ${index + 1}`}
                    selected={selectedImage === img}
                    onClick={() => setSelectedImage(img)}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Chip label={productData.category} color="primary" sx={{ mb: 2, alignSelf: 'flex-start' }} />
              <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                {productData.name}
              </Typography>
              <Typography variant="h4" component="p" color="primary" sx={{ mb: 3 }}>
                {currency}{productData.price}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
                {productData.description}
              </Typography>

              <Typography variant="h6" component="h2" fontWeight="medium" sx={{ mb: 2 }}>
                Select Size
              </Typography>
              <ToggleButtonGroup
                value={selectedSize}
                exclusive
                onChange={handleSizeChange}
                aria-label="product size"
                sx={{ mb: 4 }}
              >
                {productData.sizes.map((size) => (
                  <StyledToggleButton key={size} value={size} aria-label={size} sx={{ px: 3, py: 1 }}>
                    {size}
                  </StyledToggleButton>
                ))}
              </ToggleButtonGroup>

              {productData.sizes?.length === 0 ? (
                // If product has no sizes, show a single add to cart button
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAddToCart}
                  sx={{ mb: 4, py: 2, height: 56 }}
                >
                  {totalQuantityInCart > 0 ? `Add More (${totalQuantityInCart})` : 'Add to Cart'}
                </Button>
              ) : selectedSize ? (
                // If size is selected, show quantity controls
                totalQuantityInCart > 0 ? (
                  <ButtonGroup variant="contained" size="large" sx={{ mb: 4, height: 56 }}>
                    <Button onClick={handleRemoveFromCart}>
                      <RemoveIcon />
                    </Button>
                    <Button disabled sx={{ backgroundColor: 'white !important', color: 'black !important', fontSize: '1.2rem' }}>
                      {totalQuantityInCart}
                    </Button>
                    <Button onClick={handleAddToCart}>
                      <AddIcon />
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleAddToCart}
                    sx={{ mb: 4, py: 2, height: 56 }}
                  >
                    Add to Cart
                  </Button>
                )
              ) : (
                // If no size selected but sizes are available
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAddToCart}
                  sx={{ mb: 4, py: 2, height: 56 }}
                >
                  Add to Cart
                </Button>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="text" 
                  startIcon={<FavoriteBorderIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Wishlist functionality coming soon!');
                    // TODO: Implement wishlist functionality
                  }}
                >
                  Add to Wishlist
                </Button>
                <Button 
                  variant="text" 
                  startIcon={<ShareIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Basic share functionality
                    if (navigator.share) {
                      navigator.share({
                        title: productData.name,
                        text: `Check out ${productData.name} on our store!`,
                        url: window.location.href,
                      }).catch(console.error);
                    } else {
                      // Fallback for browsers that don't support Web Share API
                      navigator.clipboard.writeText(window.location.href);
                      // Show a message to the user
                      alert('Link copied to clipboard!');
                    }
                  }}
                >
                  Share
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ProductContainer>

      <Box sx={{ mt: 8 }}>
        <RelatedProducts category={productData.category} subCategory={productData.subCategory} />
      </Box>
    </Box>
  );
};

export default Product;
